import { motion } from 'framer-motion';
import { useState, useRef, useCallback } from 'react';
import { useTranslations } from 'next-intl';
import {
    PlayIcon,
    PauseIcon,
    SpeakerWaveIcon,
    SpeakerXMarkIcon,
    UsersIcon,
    ChartBarIcon,
    CurrencyDollarIcon,
} from '@heroicons/react/24/outline';

const containerVariants = {
    hidden: { opacity: 0, y: 18 },
    visible: (delay = 0) => ({
        opacity: 1,
        y: 0,
        transition: { duration: 0.5, ease: 'easeOut', delay },
    }),
};

const fadeInSide = (x = 0, delay = 0) => ({
    hidden: { opacity: 0, x },
    visible: {
        opacity: 1,
        x: 0,
        transition: { duration: 0.4, delay },
    },
});

function VideoCard({
                       videoRef,
                       isPlaying,
                       isMuted,
                       onTogglePlay,
                       onToggleMute,
                       videoSrc,
                       videoPoster,
                       labels,
                   }) {
    return (
        <div className="relative bg-white rounded-2xl shadow-lg overflow-hidden">
            {/* Match container to actual video ratio (1920x1300) */}
            <div className="relative w-full aspect-[1920/1355]">
                <video
                    ref={videoRef}
                    className="absolute inset-0 w-full h-full object-cover"
                    poster={videoPoster}
                    muted={isMuted}
                    loop
                    playsInline
                    preload="metadata"
                    width={1920}
                    height={1355}
                    aria-label={labels.videoAriaLabel}
                >
                    <source src={videoSrc} type="video/mp4" />
                    {labels.videoFallback}
                </video>

                {/* Overlay for readability */}
                <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/20 via-transparent to-transparent" />

                {/* Play overlay */}
                <motion.div
                    onClick={onTogglePlay}
                    className="absolute inset-0 flex items-center justify-center cursor-pointer"
                    whileHover={{ scale: 1.06 }}
                    transition={{ type: 'spring', stiffness: 280, damping: 18 }}
                >
                    {!isPlaying && (
                        <motion.div
                            className="w-20 h-20 bg-white/95 rounded-full flex items-center justify-center shadow-2xl"
                            whileHover={{ scale: 1.05 }}
                            transition={{ type: 'spring', stiffness: 280, damping: 18 }}
                        >
                            <PlayIcon className="w-8 h-8 text-blue-600 ml-0.5" />
                        </motion.div>
                    )}
                </motion.div>

                {/* Controls */}
                <div className="absolute bottom-3 right-3 flex gap-2.5 z-10">
                    <motion.button
                        type="button"
                        whileHover={{ scale: 1.08 }}
                        whileTap={{ scale: 0.95 }}
                        onClick={onToggleMute}
                        aria-label={isMuted ? labels.unmute : labels.mute}
                        className="p-2.5 bg-white/95 rounded-full shadow-md hover:bg-white"
                    >
                        {isMuted ? (
                            <SpeakerXMarkIcon className="w-4 h-4 text-gray-900" />
                        ) : (
                            <SpeakerWaveIcon className="w-4 h-4 text-gray-900" />
                        )}
                    </motion.button>

                    <motion.button
                        type="button"
                        whileHover={{ scale: 1.08 }}
                        whileTap={{ scale: 0.95 }}
                        onClick={onTogglePlay}
                        aria-label={isPlaying ? labels.pause : labels.play}
                        className="p-2.5 bg-white/95 rounded-full shadow-md hover:bg-white"
                    >
                        {isPlaying ? (
                            <PauseIcon className="w-4 h-4 text-gray-900" />
                        ) : (
                            <PlayIcon className="w-4 h-4 text-gray-900" />
                        )}
                    </motion.button>
                </div>
            </div>
        </div>
    );
}

function StepsTimeline({ steps }) {
    return (
        <motion.div
            variants={fadeInSide(15)}
            initial="hidden"
            animate="visible"
            className="bg-white rounded-2xl shadow-lg px-5 py-4 border border-gray-100 h-full flex items-center"
        >
            <div className="relative w-full">
                {/* Vertical line */}
                <div className="absolute left-7 top-1 bottom-1 w-[2px] bg-gradient-to-b from-blue-200 via-purple-200 to-green-200" />

                <div className="space-y-3">
                    {steps.map((step, index) => {
                        const Icon = step.icon;
                        const title = step.title;
                        const description = step.description;

                        return (
                            <motion.div
                                key={step.number || index}
                                initial={{ opacity: 0, x: 8 }}
                                animate={{ opacity: 1, x: 0 }}
                                transition={{ delay: index * 0.05, duration: 0.22 }}
                                className="flex items-start gap-4 group relative"
                            >
                                {/* Icon with animation */}
                                <motion.div
                                    whileHover={{
                                        scale: 1.06,
                                        rotate: 2,
                                        boxShadow:
                                            '0 10px 22px -6px rgba(59,130,246,0.35), 0 8px 14px -8px rgba(147,51,234,0.35)',
                                    }}
                                    transition={{ type: 'spring', stiffness: 260, damping: 18 }}
                                    className="flex-shrink-0 relative"
                                >
                                    <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white">
                                        {Icon && <Icon className="w-6 h-6" />}
                                    </div>

                                    {/* Step number */}
                                    <span className="absolute -top-2 -right-2 w-5 h-5 bg-blue-500 rounded-full text-[9px] flex items-center justify-center font-semibold border-2 border-white">
                                        {step.number}
                                      </span>
                                </motion.div>

                                {/* Text */}
                                <div className="flex-1">
                                    <h3 className="text-sm md:text-base font-semibold text-gray-900 mb-0.5 group-hover:text-blue-600 transition-colors">
                                        {title}
                                    </h3>
                                    {description && (
                                        <p className="text-xs md:text-sm text-gray-600 leading-snug">
                                            {description}
                                        </p>
                                    )}
                                </div>
                            </motion.div>
                        );
                    })}
                </div>
            </div>
        </motion.div>
    );
}

function HowAfivioWorksSummary({ t }) {
    return (
        <motion.section
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            custom={0.25}
            className="mt-14 bg-gradient-to-r from-blue-50 to-purple-50 rounded-3xl p-8 border border-gray-100"
        >
            <h3 className="text-2xl md:text-3xl font-bold text-center mb-8 bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                {t('summary.title')}
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {/* Card 1 */}
                <div className="text-center p-6 bg-white rounded-2xl shadow-md flex flex-col h-full">
                    <div className="w-12 h-12 bg-blue-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                        <UsersIcon className="w-7 h-7 text-blue-600" />
                    </div>
                    <h4 className="font-semibold text-lg mb-2">
                        {t('summary.cards.setup.title')}
                    </h4>
                    <p className="text-gray-600 text-sm">
                        {t('summary.cards.setup.description')}
                    </p>
                </div>

                {/* Card 2 */}
                <div className="text-center p-6 bg-white rounded-2xl shadow-md flex flex-col h-full">
                    <div className="w-12 h-12 bg-purple-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                        <ChartBarIcon className="w-7 h-7 text-purple-600" />
                    </div>
                    <h4 className="font-semibold text-lg mb-2">
                        {t('summary.cards.onboard.title')}
                    </h4>
                    <p className="text-gray-600 text-sm">
                        {t('summary.cards.onboard.description')}
                    </p>
                </div>

                {/* Card 3 */}
                <div className="text-center p-6 bg-white rounded-2xl shadow-md flex flex-col h-full">
                    <div className="w-12 h-12 bg-green-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                        <CurrencyDollarIcon className="w-7 h-7 text-green-600" />
                    </div>
                    <h4 className="font-semibold text-lg mb-2">
                        {t('summary.cards.track.title')}
                    </h4>
                    <p className="text-gray-600 text-sm">
                        {t('summary.cards.track.description')}
                    </p>
                </div>
            </div>
        </motion.section>
    );
}

export default function HowItWorksSection({
                                              title,
                                              steps,
                                              videoSrc = '/videos/how-it-works.mp4',
                                              videoPoster = '/how-it-works-poster.webp',
                                          }) {
    const videoRef = useRef(null);
    const [isMuted, setIsMuted] = useState(true);
    const [isPlaying, setIsPlaying] = useState(false);

    const t = useTranslations('howItWorks');

    const resolvedTitle = title ?? t('section.title');
    const videoLabels = {
        videoAriaLabel: t('video.ariaLabel'),
        videoFallback: t('video.fallback'),
        mute: t('video.controls.mute'),
        unmute: t('video.controls.unmute'),
        play: t('video.controls.play'),
        pause: t('video.controls.pause'),
    };

    const togglePlay = useCallback(() => {
        const video = videoRef.current;
        if (!video) return;

        if (isPlaying) {
            video.pause();
        } else {
            video.play();
        }
        setIsPlaying(!isPlaying);
    }, [isPlaying]);

    const toggleMute = useCallback(() => {
        setIsMuted((prev) => !prev);
    }, []);

    return (
        <section className="py-14 px-4 md:px-6 bg-gradient-to-br from-gray-50 to-white">
            <div className="max-w-6xl mx-auto">
                <div className="grid lg:grid-cols-2 gap-10 items-start">
                    {/* LEFT: headline + video */}
                    <motion.div
                        variants={fadeInSide(-15)}
                        initial="hidden"
                        animate="visible"
                        className="flex flex-col gap-5"
                    >
                        <h2 className="text-2xl md:text-3xl font-semibold leading-snug bg-gradient-to-r from-blue-600 via-purple-600 to-green-500 bg-clip-text text-transparent">
                            {resolvedTitle}
                        </h2>

                        <VideoCard
                            videoRef={videoRef}
                            isPlaying={isPlaying}
                            isMuted={isMuted}
                            onTogglePlay={togglePlay}
                            onToggleMute={toggleMute}
                            videoSrc={videoSrc}
                            videoPoster={videoPoster}
                            labels={videoLabels}
                        />
                    </motion.div>

                    {/* RIGHT: steps timeline (with Affiliate → Creator mapping) */}
                    <StepsTimeline steps={steps} />
                </div>

                {/* Summary block */}
                <HowAfivioWorksSummary t={t} />
            </div>
        </section>
    );
}
